import type { ManuscriptDocument } from '../../lib/pdf';
import type { PatternRunResult } from '../../lib/patterns';
import { apiRefFindingId, type ApiRefStatusKind } from '../../lib/patterns/finding-id';
import type { ReferenceAuditResult, ReferenceAuditEntry } from '../../lib/apis';
import { FindingActionButtons, actionStyles, type EditorAction } from './ActionButtons';

interface Props {
  manuscript: ManuscriptDocument | null;
  analysis: PatternRunResult | null;
  referenceAudit: ReferenceAuditResult | null;
  apiState: 'pending' | 'ready' | 'failed';
  findingActions: Record<string, EditorAction | null>;
  onFindingAction: (id: string, next: EditorAction | null) => void;
}

const SEVERITY_COLOR: Record<string, string> = {
  red: 'text-red-800 bg-red-50 border-red-200/80 ring-1 ring-inset ring-red-100',
  amber: 'text-amber-900 bg-amber-50 border-amber-200/80 ring-1 ring-inset ring-amber-100',
  info: 'text-slate-600 bg-slate-50 border-slate-200',
};

function refSeverity(entry: ReferenceAuditEntry): 'red' | 'amber' | 'info' | null {
  switch (entry.outcome.status) {
    case 'retracted':
      return 'red';
    case 'mismatch':
      return 'amber';
    case 'unresolved':
      return entry.outcome.reason === 'doi_not_found' ? 'amber' : null;
    case 'error':
      return null;
    case 'resolved':
      return null;
  }
}

function refFindingText(entry: ReferenceAuditEntry): string {
  const label = entry.referenceNumber !== null ? `Reference ${entry.referenceNumber}` : 'Reference';
  switch (entry.outcome.status) {
    case 'retracted': {
      const dois = entry.outcome.work.retractionDois.join(', ');
      return `${label}: cited as active but Crossref reports retraction (notice DOI ${dois || 'unknown'}).`;
    }
    case 'mismatch':
      return `${label}: metadata mismatch — ${entry.outcome.mismatches.join('; ')}.`;
    case 'unresolved':
      return entry.outcome.reason === 'doi_not_found'
        ? `${label}: DOI ${entry.doi ?? ''} does not resolve on Crossref.`
        : `${label}: no DOI to verify.`;
    case 'error':
      return `${label}: Crossref lookup failed (${entry.outcome.error.message}).`;
    case 'resolved':
      return `${label}: Crossref confirms.`;
  }
}

export function IntegrityFindings({
  manuscript,
  analysis,
  referenceAudit,
  apiState,
  findingActions,
  onFindingAction,
}: Props) {
  if (!manuscript || !analysis) {
    return (
      <section className="ms-card">
        <div className="ms-ribbon ms-ribbon--amber">
          <h2>Integrity findings</h2>
        </div>
        <p className="text-xs leading-relaxed text-slate-600">
          Deterministic consistency checks: sample sizes that disagree across
          Abstract/Methods/Results, inexact p-values reported without an exact value, and
          abstract &ldquo;spin&rdquo; phrasing — plus the Crossref reference audit
          (retractions, DOI &amp; metadata mismatches) and voice-pattern catalogue flags.
          Tuned conservatively so a missed nuance is preferred over a false alarm; every flag
          cites the passage for you to verify.
        </p>
      </section>
    );
  }

  const stats = analysis.findings.filter((f) => f.category === 'stats');
  const voice = analysis.findings.filter((f) => f.category === 'voice');
  const patternRefFindings = analysis.findings.filter((f) => f.category === 'references');

  // API reference-audit findings get stable IDs (apiRefFindingId) so editor
  // confirm/dismiss actions persist (Sprint 8b).
  const apiRefFindings: Array<{ id: string; severity: 'red' | 'amber' | 'info'; findingText: string; evidencePassage: string }> = [];
  if (referenceAudit) {
    for (const entry of referenceAudit.entries) {
      const sev = refSeverity(entry);
      if (!sev) continue;
      const statusKind: ApiRefStatusKind | null =
        entry.outcome.status === 'retracted'
          ? 'retracted'
          : entry.outcome.status === 'mismatch'
            ? 'mismatch'
            : entry.outcome.status === 'unresolved' && entry.outcome.reason === 'doi_not_found'
              ? 'not_found'
              : null;
      if (!statusKind) continue;
      const doi = entry.doi ?? entry.rawTitle.slice(0, 80);
      apiRefFindings.push({
        id: apiRefFindingId(doi, statusKind),
        severity: sev,
        findingText: refFindingText(entry),
        evidencePassage: '',
      });
    }
  }

  const refResolvedCount =
    referenceAudit?.entries.filter((e) => e.outcome.status === 'resolved').length ?? 0;
  const refTotal = referenceAudit?.entries.length ?? 0;

  function renderFinding(
    f: { id: string; severity: 'red' | 'amber' | 'info'; findingText: string; evidencePassage: string },
  ) {
    const action = findingActions[f.id] ?? null;
    const styles = actionStyles(action);
    return (
      <li
        key={f.id}
        className={`flex items-start justify-between gap-2 rounded-lg border px-2.5 py-1.5 text-[11px] ${SEVERITY_COLOR[f.severity]} ${styles.container}`}
      >
        <div className={`flex-1 ${styles.text}`}>
          {f.findingText}
          {f.evidencePassage && (
            <div className="mt-0.5 text-[10px] italic text-slate-500">
              &ldquo;{f.evidencePassage}&rdquo;
            </div>
          )}
        </div>
        <FindingActionButtons
          action={action}
          onChange={(next) => onFindingAction(f.id, next)}
          compact
        />
      </li>
    );
  }

  return (
    <section className="ms-card">
      <div className="ms-ribbon ms-ribbon--amber">
        <h2>Integrity findings</h2>
      </div>

      {stats.length > 0 && (
        <FindingGroup name={`Statistics (${stats.length})`}>
          {stats.slice(0, 4).map(renderFinding)}
          {stats.length > 4 && (
            <li className="text-[11px] text-slate-500">… and {stats.length - 4} more</li>
          )}
        </FindingGroup>
      )}

      <FindingGroup
        name={`References (${apiRefFindings.length + patternRefFindings.length})`}
        empty={apiRefFindings.length + patternRefFindings.length === 0}
      >
        {apiState === 'pending' && (
          <li className="text-[11px] text-slate-500">Crossref lookups in progress…</li>
        )}
        {apiState === 'ready' && referenceAudit?.degraded && (
          <li className="rounded border border-amber-300 bg-amber-50 px-2 py-1 text-[11px] text-amber-900">
            Crossref was unreachable for some lookups. Reference audit is partial.
          </li>
        )}
        {apiState === 'ready' && refTotal > 0 && (
          <li className="text-[11px] text-slate-500">
            Crossref verified {refResolvedCount} of {refTotal} references.
          </li>
        )}
        {patternRefFindings.slice(0, 3).map(renderFinding)}
        {apiRefFindings.slice(0, 6).map(renderFinding)}
        {apiRefFindings.length > 6 && (
          <li className="text-[11px] text-slate-500">… and {apiRefFindings.length - 6} more</li>
        )}
      </FindingGroup>

      {voice.length > 0 && (
        <FindingGroup name={`Voice patterns (${voice.length})`}>
          {voice.slice(0, 4).map(renderFinding)}
          {voice.length > 4 && (
            <li className="text-[11px] text-slate-500">… and {voice.length - 4} more</li>
          )}
        </FindingGroup>
      )}

      <p className="mt-2 text-[10px] text-slate-400">
        Voice findings are observed patterns the editor may want to verify — not AI-detection
        scores.
      </p>
    </section>
  );
}

interface GroupProps {
  name: string;
  empty?: boolean;
  children: React.ReactNode;
}

function FindingGroup({ name, empty, children }: GroupProps) {
  if (empty) return null;
  return (
    <div className="mt-3">
      <p className="ms-eyebrow">{name}</p>
      <ul className="mt-1.5 space-y-1.5">{children}</ul>
    </div>
  );
}
